import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  ReservationSlotTargetShift,
  ReservationSlotGenerationResult,
  ReservationSlotOverride,
} from '../../../../domain/reservationSlots';
import {
  buildOverrideTemplateMap,
  resolveTemplateIdForDoctor,
  resolveInitialAssignments,
} from '../../../../domain/reservationSlots';
import type { ReservationSlotRepository } from '../../../../application/reservationSlots/ports/ReservationSlotRepository';
import {
  getTargetShifts,
  generateReservationSlots,
} from '../../../../application/reservationSlots';

/**
 * doctorShiftId をキーとした割り当てテンプレートIDのマップ
 */
export type AssignmentMap = Record<number, string>;

/**
 * useReservationSlotGeneration フックの戻り値型
 */
export interface UseReservationSlotGenerationReturn {
  year: number;
  month: number;
  targetShifts: ReservationSlotTargetShift[];
  assignments: AssignmentMap;
  manualShiftIds: ReadonlySet<number>;
  overrideMap: ReadonlyMap<number, string>;
  defaultTemplateId: string | null;
  isLoading: boolean;
  generating: boolean;
  error: string | null;
  result: ReservationSlotGenerationResult | null;
  goToPreviousMonth: () => void;
  goToNextMonth: () => void;
  setYearMonth: (year: number, month: number) => void;
  setAssignment: (doctorShiftId: number, templateId: string) => void;
  applyTemplateToAll: (templateId: string) => void;
  resetToSettings: () => void;
  generate: () => Promise<boolean>;
  clearError: () => void;
  clearResult: () => void;
}

/**
 * 解放枠生成タブ用カスタムフック
 *
 * @description
 * 対象月の確定済み医師シフトを取得し、各シフトへ適用するテンプレートを
 * 割り当てて解放枠を一括生成する。初期割り当ての優先順位は
 * 生成済みテンプレート（sourceTemplateId） > 医師個別の上書き > デフォルトテンプレート。
 * 画面上で手動変更された行は manualShiftIds で追跡し、適用元バッジの表示と
 * resetToSettings（設定由来の割り当てへの復帰）に使用する。
 *
 * @param {ReservationSlotRepository} repository - DIされるRepository実装
 * @param {string | null} defaultTemplateId - 個別上書きのない医師に使うデフォルトテンプレートID
 * @param {readonly ReservationSlotOverride[]} overrides - 医師個別の上書き設定
 */
export const useReservationSlotGeneration = (
  repository: ReservationSlotRepository,
  defaultTemplateId: string | null,
  overrides: readonly ReservationSlotOverride[]
): UseReservationSlotGenerationReturn => {
  const now = new Date();
  const [year, setYear] = useState<number>(now.getFullYear());
  const [month, setMonth] = useState<number>(now.getMonth() + 1);
  const [targetShifts, setTargetShifts] = useState<ReservationSlotTargetShift[]>([]);
  const [assignments, setAssignments] = useState<AssignmentMap>({});
  // 画面上で手動変更されたシフト（適用元バッジ「手動変更」の判定に使用）
  const [manualShiftIds, setManualShiftIds] = useState<Set<number>>(() => new Set());
  const [isLoading, setIsLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReservationSlotGenerationResult | null>(null);

  // doctorId → templateId の上書きマップ（overridesの内容が変わったときのみ再生成）
  const overrideMap = useMemo(() => buildOverrideTemplateMap(overrides), [overrides]);

  /**
   * 対象年月の生成対象シフトを取得して割り当てを初期化する
   *
   * @description
   * 生成直後の再取得からも呼ばれるため、ここでは生成結果（result）をクリアしない。
   * クリアするのは「月の切り替え時」と「生成の開始時」だけとする。
   */
  const loadTargetShifts = useCallback(
    async (y: number, m: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const shifts = await getTargetShifts(repository, y, m);
        setTargetShifts(shifts);
        // 初期割り当て: 生成済み > 個別上書き > デフォルト（いずれも無ければ空）
        setAssignments(resolveInitialAssignments(shifts, overrideMap, defaultTemplateId));
        // 取得し直した時点で手動変更は無かったことになる
        setManualShiftIds(new Set());
      } catch (e) {
        setError(e instanceof Error ? e.message : '生成対象シフトの取得に失敗しました');
        setTargetShifts([]);
        setAssignments({});
        setManualShiftIds(new Set());
      } finally {
        setIsLoading(false);
      }
    },
    [repository, defaultTemplateId, overrideMap]
  );

  useEffect(() => {
    // 月が変わると前月の生成結果は無関係になるため、ここでクリアする
    setResult(null);
    loadTargetShifts(year, month);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, month]);

  // 基本設定（デフォルト・個別上書き）が後から確定した場合、
  // 未割り当て（かつ生成済みでない）シフトへ補完する。
  // 既に値が入っている行は手動選択の可能性があるため上書きしない。
  useEffect(() => {
    if (!defaultTemplateId && overrideMap.size === 0) {
      return;
    }
    setAssignments((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const s of targetShifts) {
        if (next[s.doctorShiftId] || s.sourceTemplateId) {
          continue;
        }
        const resolved = resolveTemplateIdForDoctor(s.doctorId, overrideMap, defaultTemplateId);
        if (resolved) {
          next[s.doctorShiftId] = resolved;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [defaultTemplateId, overrideMap, targetShifts]);

  const goToPreviousMonth = useCallback(() => {
    setYear((prevYear) => (month === 1 ? prevYear - 1 : prevYear));
    setMonth((prevMonth) => (prevMonth === 1 ? 12 : prevMonth - 1));
  }, [month]);

  const goToNextMonth = useCallback(() => {
    setYear((prevYear) => (month === 12 ? prevYear + 1 : prevYear));
    setMonth((prevMonth) => (prevMonth === 12 ? 1 : prevMonth + 1));
  }, [month]);

  /**
   * 対象年月を外部から指定する
   *
   * @description
   * 月スコープの解放枠設定ビューへ遷移するとき、遷移元で選んだ月を反映するために使う。
   * 年月が変わると対象シフトが再取得され、手動変更もリセットされる。
   */
  const setYearMonth = useCallback((nextYear: number, nextMonth: number) => {
    setYear(nextYear);
    setMonth(nextMonth);
  }, []);

  const setAssignment = useCallback((doctorShiftId: number, templateId: string) => {
    setAssignments((prev) => ({ ...prev, [doctorShiftId]: templateId }));
    setManualShiftIds((prev) => {
      if (prev.has(doctorShiftId)) {
        return prev;
      }
      const next = new Set(prev);
      next.add(doctorShiftId);
      return next;
    });
  }, []);

  const applyTemplateToAll = useCallback(
    (templateId: string) => {
      setAssignments(() => {
        const next: AssignmentMap = {};
        for (const s of targetShifts) {
          next[s.doctorShiftId] = templateId;
        }
        return next;
      });
      // 一括適用も設定由来ではない上書きのため、全行を手動変更として扱う
      setManualShiftIds(new Set(targetShifts.map((s) => s.doctorShiftId)));
    },
    [targetShifts]
  );

  const resetToSettings = useCallback(() => {
    setManualShiftIds(new Set());
    setAssignments(resolveInitialAssignments(targetShifts, overrideMap, defaultTemplateId));
  }, [targetShifts, overrideMap, defaultTemplateId]);

  const generate = useCallback(async (): Promise<boolean> => {
    if (targetShifts.length === 0) {
      setError('生成対象のシフトがありません');
      return false;
    }
    const assignmentList = targetShifts.map((s) => ({
      doctorShiftId: s.doctorShiftId,
      templateId: assignments[s.doctorShiftId] ?? '',
    }));
    if (assignmentList.some((a) => !a.templateId)) {
      setError('テンプレートが未選択のシフトがあります');
      return false;
    }

    setGenerating(true);
    setError(null);
    setResult(null);
    try {
      const genResult = await generateReservationSlots(repository, {
        year,
        month,
        assignments: assignmentList,
      });
      setResult(genResult);
      // 生成後は最新のsourceTemplateIdを反映するため再取得
      await loadTargetShifts(year, month);
      return true;
    } catch (e) {
      setError(e instanceof Error ? e.message : '解放枠の生成に失敗しました');
      return false;
    } finally {
      setGenerating(false);
    }
  }, [repository, year, month, targetShifts, assignments, loadTargetShifts]);

  const clearError = useCallback(() => setError(null), []);
  const clearResult = useCallback(() => setResult(null), []);

  return {
    year,
    month,
    targetShifts,
    assignments,
    manualShiftIds,
    overrideMap,
    defaultTemplateId,
    isLoading,
    generating,
    error,
    result,
    goToPreviousMonth,
    goToNextMonth,
    setYearMonth,
    setAssignment,
    applyTemplateToAll,
    resetToSettings,
    generate,
    clearError,
    clearResult,
  };
};
